import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../utils/animations';
import { technologies } from '../data/content';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const Technologies: React.FC = () => {
  const TechSection = ({ title, techs, bgColor }: { title: string; techs: any[]; bgColor: string }) => (
    <motion.div
      className={`rounded-2xl p-6 ${bgColor}`}
      variants={itemVariants}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{title}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {techs.map((tech, index) => (
          <motion.div
            key={tech.name}
            className="bg-white rounded-lg p-4 text-center shadow-sm border border-gray-100 group hover:shadow-md transition-all duration-200"
            whileHover={{ y: -5 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="text-3xl mb-2 group-hover:scale-110 transition-transform duration-200">
              {tech.icon}
            </div>
            <div className="text-sm font-medium text-gray-800">{tech.name}</div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <>
      <Helmet>
        <title>Technologies We Use - SOFTCONCERN TECHNOLOGIES</title>
        <meta name="description" content="Explore the modern technologies and tools we use to build robust and scalable IT solutions." />
        <meta name="keywords" content="React, Node.js, Python, AWS, cloud technologies, development tools" />
        <meta property="og:title" content="Technologies We Use - SOFTCONCERN TECHNOLOGIES" />
        <meta property="og:description" content="Explore the modern technologies and tools we use to build robust and scalable IT solutions." />
        <link rel="canonical" href="/technologies" />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-white">
        <div className="container">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              variants={itemVariants}
            >
              Technologies <span className="text-primary-600">We Use</span>
            </motion.h1>
            <motion.p 
              className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto"
              variants={itemVariants}
            >
              We leverage modern technologies to deliver robust and scalable solutions that meet your business needs.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Technologies Grid */}
      <section className="section-padding bg-white">
        <div className="container">
          <motion.div
            className="space-y-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <TechSection 
              title="Frontend Technologies" 
              techs={technologies.frontend} 
              bgColor="bg-blue-50" 
            />
            
            <TechSection 
              title="Backend Technologies" 
              techs={technologies.backend} 
              bgColor="bg-green-50" 
            />
            
            <TechSection 
              title="Database Solutions" 
              techs={technologies.database} 
              bgColor="bg-purple-50" 
            />
            
            <TechSection 
              title="Cloud Platforms" 
              techs={technologies.cloud} 
              bgColor="bg-orange-50" 
            />
            
            <TechSection 
              title="DevOps Tools" 
              techs={technologies.devops} 
              bgColor="bg-indigo-50" 
            />
            
            <TechSection 
              title="AI & Automation Tools" 
              techs={technologies.aiTools} 
              bgColor="bg-pink-50" 
            />
          </motion.div>
        </div>
      </section>

      {/* Why These Technologies Section */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why We Choose These Technologies
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Our technology stack is carefully selected to ensure we deliver high-quality, 
                scalable, and maintainable solutions that stand the test of time.
              </p>
              
              <div className="space-y-4">
                {[
                  'Performance and scalability',
                  'Security and reliability',
                  'Developer productivity',
                  'Community support and ecosystem',
                  'Future-proof architecture',
                  'Cost-effective solutions'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">⚡</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Cutting-Edge Technology
                  </h3>
                  <p className="text-gray-600">
                    We stay up-to-date with the latest technology trends to provide innovative solutions
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary-600 mb-1">15+</div>
                    <div className="text-sm text-gray-600">Technologies</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600 mb-1">99%</div>
                    <div className="text-sm text-gray-600">Uptime</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600 mb-1">24/7</div>
                    <div className="text-sm text-gray-600">Monitoring</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600 mb-1">100%</div>
                    <div className="text-sm text-gray-600">Secure</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="container">
          <motion.div
            className="text-center text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Build with Modern Technology?
            </h2>
            <p className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
              Let's discuss how we can leverage these technologies to build your next project.
            </p>
            <Link
              to="/contact"
              className="bg-white text-primary-600 hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center space-x-2"
            >
              <span>Start Your Project</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Technologies;